/**
 * HelixPresetPicker — choose a preset by NAME (DEC-151, serving DEC-150).
 *
 * The notepad calls picking a preset by name rather than by number "the
 * single biggest novice win" in the whole Helix feature, and this is it:
 * pick `Essex A15` from a list instead of working out that it is setlist 1,
 * preset 6. On pick it writes `setlist` + `program` + `presetName` together,
 * so the tile's face reads the name and keeps reading it with the bridge
 * offline (that is what `presetName` caches for).
 *
 * ## Chassis is borrowed, deliberately
 *
 * Reuses `.matrix-overlay` + the `.backup-picker-*` modal classes rather
 * than minting a parallel set. They are already `%`-sized (`min(560px, 92%)`
 * / `max-height: 86%`), which is what the iPad-bound modal lock requires,
 * and they are already skinned. Only the two-column body grid is new CSS.
 * Mounted via `shellPortal()` per that same lock — a bare `<Portal>` here
 * would escape the iPad frame.
 *
 * ## Opened from the Inspector, mounted by App (2026-09-29)
 *
 * The open state lives HERE, not in the Inspector, and App mounts
 * {@link HelixPresetPickerHost} beside the Lights manager and counts it in
 * `anyModalOpen`, so the Inspector steps aside while it is up. The Inspector
 * sits outside `.shell`, and a shell-mounted modal cannot cover it: measured
 * by `_probe_inspector_modal_stacking`, the picker's close button sat UNDER
 * the Inspector in a true portrait frame while it was mounted by the
 * Inspector itself. The Lights manager, opened the same way but already
 * hidden-Inspector, passed in both orientations - so this follows it and the
 * iPad modal lock holds without an exception.
 *
 * ## Refresh is a loaded gun and is labelled as one
 *
 * Reading the library takes ~10s of USB and, until openhx's session-close
 * packet is found, leaves the pedal ignoring MIDI Program Changes until it
 * is power-cycled (DEC-150). So there is no auto-fetch anywhere in this
 * feature: the operator presses Refresh, having been told what it costs.
 *
 * ## An empty picker must never look like an empty pedal
 *
 * Every not-ready state gets its own sentence naming the actual cause and
 * the actual fix. Collapsing them into "no presets" is the failure the
 * Stream Deck stub shipped for a year, and it would be worse here — the
 * operator would conclude their Helix was not seen at all.
 */

import { createSignal, createEffect, For, Show, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import { shellPortal } from '../layout/shellPortal.ts';
import { editItem } from '../layout/store.ts';
import { helixSlotLabel } from './PresetChange.tsx';
import {
  helixStatus, helixError, helixHasLibrary, helixLibrary, helixStallNotice,
  helixSetlists, helixSetlistName, helixSetlistNamed, helixPresetsIn,
  helixSetlistNameSource, setHelixSetlistOverride,
  loadHelixLibrary, probeHelixDevice,
  helixDevicePresent, refreshHelixDeviceStatus, HELIX_HX_EDIT_OPEN,
} from '../helix/library.ts';

interface HelixPresetPickerProps {
  itemId: string;
  /** Current setlist on the item, so the picker opens where the author is. */
  setlist: number;
  onClose: () => void;
}

/** Which item the picker is choosing for, or null when closed. */
const [pickFor, setPickFor] = createSignal<{ itemId: string; setlist: number } | null>(null);
export const helixPickerOpen = (): boolean => pickFor() !== null;
export function openHelixPicker(itemId: string, setlist: number): void { setPickFor({ itemId, setlist }); }
export function closeHelixPicker(): void { setPickFor(null); }

/** Mounted once, by App. See the file header for why not by the Inspector. */
export function HelixPresetPickerHost() {
  return (
    <Show when={pickFor()}>
      {(p) => <HelixPresetPicker itemId={p().itemId} setlist={p().setlist} onClose={closeHelixPicker} />}
    </Show>
  );
}

export function HelixPresetPicker(props: HelixPresetPickerProps) {
  const [activeSetlist, setActiveSetlist] = createSignal(props.setlist);
  /** Setlist names are typed here when the device cannot report them.
   *  Eight strings, changed about never — a completely different problem
   *  from the 1024 preset names, which must be read automatically. If
   *  OPBridge ever returns real setlist names they win and this becomes
   *  redundant on its own (see `helixSetlistName`). */
  const [editingNames, setEditingNames] = createSignal(false);

  /**
   * Read progress — a TIMED estimate, not real progress.
   *
   * `POST /helix/probe` is one HTTP call that reports nothing until it
   * returns, so there is no true percentage to show. OPBridge measured the
   * full 1024-preset read at 7.9s; this eases over 10s to leave headroom.
   *
   * ## It must never claim to be finished before it is
   *
   * The curve is ease-out cubic capped at 94%, so a slow read parks just shy
   * of full rather than sitting at 100% while nothing happens — the single
   * most common way a progress bar becomes a liar. Completion snaps it to
   * 100% with a short transition, which reads as "it finished" rather than
   * "it jumped".
   *
   * Driven by rAF and applied as `transform: scaleX()`, so the animation
   * lives on the compositor and stays smooth while the main thread is busy
   * parsing 1024 presets.
   */
  const [progress, setProgress] = createSignal(0);
  const [done, setDone] = createSignal(false);
  const [elapsed, setElapsed] = createSignal(0);

  const EST_MS = 10_000;
  const CAP = 0.94;

  createEffect(() => {
    if (helixStatus() !== 'probing') {
      // Leaving 'probing' with a bar on screen means the read ended. A read
      // that worked completes visually rather than vanishing mid-sweep; one
      // that failed goes, and its message says why (it used to say "Done"
      // over the old names, operator 2026-10-10).
      if (progress() > 0 && helixStatus() !== 'ready') { setProgress(0); setDone(false); setElapsed(0); return; }
      if (progress() > 0) {
        setDone(true);
        setProgress(1);
        const t = setTimeout(() => { setProgress(0); setDone(false); setElapsed(0); }, 700);
        onCleanup(() => clearTimeout(t));
      }
      return;
    }
    setDone(false);
    const start = performance.now();
    let raf = 0;
    const tick = (now: number): void => {
      const t = Math.min(1, (now - start) / EST_MS);
      setElapsed((now - start) / 1000);
      setProgress(CAP * (1 - Math.pow(1 - t, 3)));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    onCleanup(() => cancelAnimationFrame(raf));
  });

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') props.onClose();
  }
  onMount(() => {
    window.addEventListener('keydown', onKey);
    // Cheap cached GET — never touches USB, so opening the picker may do it.
    // This is what surfaces names probed in an earlier session without the
    // operator pressing anything. The PROBE stays behind the button.
    // Every open, not only with no names: names read from OPBridge's own
    // screen otherwise never reached a picker that already had some
    // (operator, 2026-10-10).
    void loadHelixLibrary();
    // Also enumeration-only - refresh whether the pedal is actually there,
    // so "Read from pedal" can say so instead of spending 8s of USB to
    // discover it.
    refreshHelixDeviceStatus();
  });
  onCleanup(() => window.removeEventListener('keydown', onKey));

  /** Writes all three fields at once. `presetName` is the cache that keeps
   *  the face readable when the bridge is gone — writing setlist/program
   *  without it would leave the tile showing "02B" forever. */
  function choose(pc: number, name: string): void {
    editItem(props.itemId, {
      setlist: activeSetlist(),
      program: pc,
      presetName: name,
    });
    props.onClose();
  }

  /** One sentence per state, each naming a cause and a fix. The
   *  `never-probed` case is the one that matters most: OPBridge is fine and
   *  the pedal IS present, so telling the operator to check their cable
   *  (which the first version of this did) sends them hunting a fault that
   *  does not exist. The fix is one press of the button below. */
  const emptyMessage = (): string => {
    switch (helixStatus()) {
      case 'loading':
        return 'Checking what OPBridge has cached…';
      case 'probing':
        return 'Reading the pedal over USB… about 8 seconds for all 1024 presets.';
      case 'never-probed':
        return 'Your Helix is connected, but its names have not been read yet. Press “Read from pedal” below - it takes about 8 seconds and you only do it once per session.';
      case 'no-bridge':
        return 'No OPBridge connected, so there is nothing to ask. Connect one and try again.';
      case 'unavailable':
        return helixError() || 'The preset library is not available from this OPBridge.';
      case 'error':
        // A failed read already says it in full.
        return helixError() === HELIX_HX_EDIT_OPEN || helixError().startsWith('Couldn\'t read the pedal')
          ? helixError()
          : `Could not read the library - ${helixError()}`;
      default:
        return 'No preset names yet. Press “Read from pedal” below to fetch them.';
    }
  };

  return (
    <Portal mount={shellPortal() ?? document.body}>
      <div
        class="matrix-overlay"
        onClick={() => props.onClose()}
        role="dialog"
        aria-modal="true"
        aria-label="Pick a Helix preset by name"
      >
        <div class="backup-picker-modal" onClick={(e) => e.stopPropagation()}>
          <header class="matrix-head">
            <div class="matrix-title">
              PICK A PRESET{helixLibrary().device ? ` · ${helixLibrary().device}` : ''}
            </div>
            <button
              type="button"
              class="matrix-close"
              onClick={() => props.onClose()}
              title="Close (Esc)"
              aria-label="Close"
            >
              ×
            </button>
          </header>

          <div class="backup-picker-body">
            <Show
              when={helixHasLibrary()}
              fallback={<div class="backup-picker-empty">{emptyMessage()}</div>}
            >
              <div class="helix-picker-grid">
                <div class="helix-picker-col">
                  <div class="backup-picker-sub">
                    Setlist
                    <button
                      type="button"
                      class="inspector-pill"
                      style={{ 'margin-left': '8px' }}
                      onClick={() => setEditingNames(!editingNames())}
                      title="Type your setlist names. The Helix does not report them over USB, so OPButtons cannot read them automatically."
                    >
                      {editingNames() ? 'Done' : 'Name them'}
                    </button>
                  </div>
                  {/* The Helix does not report setlist NAMES over USB — the
                      protocol has no known resource for them, so a probe can
                      never fill these in. Without this line the eight grey
                      "Setlist N" rows read as a failed import rather than a
                      thing the device cannot tell us. Eight strings typed once
                      is a different proposition from 1024 preset names, which
                      must be automatic. */}
                  <Show when={!editingNames() && !helixSetlists().some(helixSetlistNamed)}>
                    <div class="backup-picker-sub" style={{ opacity: '0.75' }}>
                      Your Helix doesn’t send setlist names over USB — tap
                      <strong> Name them</strong> and type them once.
                    </div>
                  </Show>
                  <ul class="backup-picker-list">
                    {/* for-index-ok: `helixSetlists()` returns `number[]`
                        (library.ts:237), so <For> keys these rows on
                        PRIMITIVE values, not object references. Typing an
                        override cannot change a row's identity the way a
                        spread over an object list would, so the row is never
                        remounted and the input keeps focus. Verified by
                        reading the return type, not assumed - if this ever
                        becomes an array of objects, delete this comment and
                        switch to <Index>. */}
                    <For each={helixSetlists()}>
                      {(sl) => (
                        <li>
                          <Show
                            when={!editingNames()}
                            fallback={(
                              <input
                                type="text"
                                value={helixSetlistNameSource(sl) === 'placeholder' ? '' : helixSetlistName(sl)}
                                placeholder={`Setlist ${sl + 1}`}
                                disabled={helixSetlistNameSource(sl) === 'device'}
                                title={helixSetlistNameSource(sl) === 'device'
                                  ? 'Read from the pedal - cannot be overridden'
                                  : 'Type the name as it reads on the unit'}
                                onInput={(e) => setHelixSetlistOverride(sl, e.currentTarget.value)}
                              />
                            )}
                          >
                            <button
                              type="button"
                              class="backup-picker-row"
                              data-selected={activeSetlist() === sl ? 'true' : 'false'}
                              onClick={() => setActiveSetlist(sl)}
                            >
                              {/* A placeholder is dimmed so "Setlist 3" is
                                  never mistaken for a real name. A TYPED name
                                  renders at full strength but is marked, so
                                  the operator can tell what came from the
                                  pedal and what they entered themselves. */}
                              <span
                                class="backup-picker-row-stamp"
                                style={helixSetlistNamed(sl) ? undefined : { opacity: '0.55' }}
                              >
                                {helixSetlistName(sl)}
                              </span>
                              <Show when={helixSetlistNameSource(sl) === 'typed'}>
                                <span style={{ opacity: '0.45' }}>typed</span>
                              </Show>
                            </button>
                          </Show>
                        </li>
                      )}
                    </For>
                  </ul>
                </div>

                <div class="helix-picker-col">
                  <div class="backup-picker-sub">
                    Preset — {helixPresetsIn(activeSetlist()).length} named
                  </div>
                  <Show
                    when={helixPresetsIn(activeSetlist()).length > 0}
                    fallback={(
                      <div class="backup-picker-empty">
                        No names read for this setlist.
                      </div>
                    )}
                  >
                    <ul class="backup-picker-list">
                      <For each={helixPresetsIn(activeSetlist())}>
                        {(p) => (
                          <li>
                            <button
                              type="button"
                              class="backup-picker-row"
                              onClick={() => choose(p.pc, p.name)}
                            >
                              <span class="backup-picker-row-stamp">{p.name}</span>
                              {/* The unit prints bank+letter, so echo what
                                  the player reads off the pedal. */}
                              <span style={{ opacity: '0.55' }}>{helixSlotLabel(p.pc)}</span>
                            </button>
                          </li>
                        )}
                      </For>
                    </ul>
                  </Show>
                </div>
              </div>
            </Show>

            {/* The stall is real and OPBridge reports it explicitly
                (`midi_program_changes_stalled`). Showing it AFTER the probe,
                in its own block, is the difference between the operator
                knowing to replug the cable and discovering a dead preset
                button later. */}
            {/* Read progress. Rendered whenever the bar is live OR finishing,
                so the completion snap is visible instead of the whole thing
                disappearing the instant the request returns. */}
            <Show when={progress() > 0}>
              <div class="helix-progress-wrap">
                <div class="helix-progress">
                  <div
                    class="helix-progress-fill"
                    data-done={done() ? 'true' : 'false'}
                    style={{ transform: `scaleX(${progress()})` }}
                  />
                </div>
                <div class="helix-progress-label">
                  {done()
                    ? `Done - ${Object.keys(helixLibrary().presetNames).length} presets read`
                    : `Reading all eight setlists… ${elapsed().toFixed(1)}s`}
                </div>
              </div>
            </Show>

            {/* A failed read with names already listed: the list stays (it is
                still right for the pedal as it was), and this says why the
                read did not happen. With no names, the empty message says it. */}
            <Show when={helixHasLibrary() && (helixStatus() === 'error' || helixStatus() === 'unavailable') && helixError()}>
              <div class="backup-picker-empty backup-picker-error" data-helix-read-error>
                {helixError()}
              </div>
            </Show>

            <Show when={helixStallNotice()}>
              <div class="backup-picker-empty backup-picker-error">
                {helixStallNotice()}
              </div>
            </Show>

            <p class="backup-picker-sub">
              Reading takes about 8 seconds. Read once per session, then everything runs
              over MIDI. OPButtons clears OPBridge’s side for you automatically, and OPBridge
              closes the <strong>pedal</strong>’s session after each read, so no USB replug is
              needed. If one ever is, a notice above says so.
            </p>
            <button
              type="button"
              class="b"
              disabled={
                helixStatus() === 'probing' || helixStatus() === 'loading'
                || helixDevicePresent() === false
              }
              title={helixDevicePresent() === false
                ? 'OPBridge does not see a Helix on USB right now.'
                : undefined}
              onClick={() => void probeHelixDevice()}
            >
              {helixStatus() === 'probing'
                ? 'Reading the pedal…'
                : helixDevicePresent() === false
                  ? 'Helix not detected'
                  : 'Read from pedal'}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
